import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@context/AuthContext";
import {
  connectiqGetProfileSessions,
  connectiqGetProfileHistory,
  connectiqCreateProfileSession,
  connectiqProfileChat,
  connectiqDeleteSession,
} from "@services/connectiqAPI";

// ── Time helper ───────────────────────────────────────────────────────────────
const timeAgo = (d) => {
  if (!d) return "";
  const diff = Math.floor((Date.now() - new Date(d)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
};

// ── Rich markdown renderer (matches old code exactly) ─────────────────────────
function renderTextContent(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let i = 0;

  const parseInline = (str) => {
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, idx) => {
      if (p.startsWith("**") && p.endsWith("**"))
        return <strong key={idx} style={{ fontWeight:"600", color:"var(--fc-text-primary)" }}>{p.slice(2,-2)}</strong>;
      const ip = p.split(/(\*[^*]+\*)/g);
      return ip.map((x, xi) =>
        x.startsWith("*") && x.endsWith("*")
          ? <em key={xi} style={{ color:"var(--fc-text-secondary)" }}>{x.slice(1,-1)}</em>
          : x
      );
    });
  };

  const isTableRow = (l) => l.trim().startsWith("|") && l.trim().endsWith("|");
  const isTableSep = (l) => /^\|[-| :]+\|$/.test(l.trim());

  while (i < lines.length) {
    const trimmed = lines[i].trim();

    if (trimmed === "---" || trimmed === "***") {
      elements.push(<div key={i} style={{ borderTop:"0.5px solid var(--fc-border-light)", margin:"12px 0" }} />);
      i++; continue;
    }
    if (isTableRow(trimmed)) {
      const tableLines = [];
      while (i < lines.length && isTableRow(lines[i].trim())) { tableLines.push(lines[i].trim()); i++; }
      const rows = tableLines.filter(r => !isTableSep(r));
      if (rows.length > 0) {
        const headers = rows[0].split("|").filter(c=>c.trim()).map(c=>c.trim());
        const bodyRows = rows.slice(1);
        elements.push(
          <div key={`t${i}`} style={{ margin:"8px 0 10px", borderRadius:8, overflow:"hidden", border:"0.5px solid var(--fc-border-light)" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"12.5px" }}>
              <thead>
                <tr>
                  {headers.map((h,hi) => (
                    <th key={hi} style={{ textAlign:"left", padding:"8px 12px", background:"var(--fc-bg-light)", fontWeight:"500", color:"var(--fc-text-secondary)", borderBottom:"0.5px solid var(--fc-border-light)" }}>
                      {parseInline(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row,ri) => {
                  const cells = row.split("|").filter(c=>c.trim()).map(c=>c.trim());
                  return (
                    <tr key={ri} style={{ background: ri%2===0?"var(--fc-card-bg)":"var(--fc-bg-light)" }}>
                      {cells.map((cell,ci) => (
                        <td key={ci} style={{ padding:"7px 12px", borderBottom:ri<bodyRows.length-1?"0.5px solid var(--fc-border-light)":"none", color:"var(--fc-text-primary)", verticalAlign:"top", lineHeight:"1.5" }}>
                          {parseInline(cell)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }
    if (trimmed.startsWith("## ")) {
      elements.push(
        <div key={i} style={{ fontWeight:"600", fontSize:"13px", color:"var(--fc-text-primary)", marginTop:14, marginBottom:5, display:"flex", alignItems:"center", gap:7 }}>
          <div style={{ width:3, height:14, background:"#2563EB", borderRadius:2, flexShrink:0 }} />
          {parseInline(trimmed.slice(3))}
        </div>
      );
      i++; continue;
    }
    if (trimmed.startsWith("### ")) {
      elements.push(
        <div key={i} style={{ fontWeight:"500", fontSize:"11px", color:"var(--fc-text-secondary)", marginTop:10, marginBottom:3, textTransform:"uppercase", letterSpacing:"0.04em" }}>
          {parseInline(trimmed.slice(4))}
        </div>
      );
      i++; continue;
    }
    if (trimmed.startsWith("#### ")) {
      elements.push(
        <div key={i} style={{ fontWeight:"600", fontSize:"13px", color:"var(--fc-text-primary)", marginTop:10, marginBottom:3 }}>
          {parseInline(trimmed.slice(5))}
        </div>
      );
      i++; continue;
    }
    if (/^\d+\.\s/.test(trimmed)) {
      const num = trimmed.match(/^(\d+)\./)[1];
      elements.push(
        <div key={i} style={{ display:"flex", gap:10, marginBottom:5, alignItems:"flex-start" }}>
          <div style={{ width:20, height:20, borderRadius:"50%", background:"var(--fc-primary-light)", color:"#2563EB", fontSize:11, fontWeight:"600", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
            {num}
          </div>
          <span style={{ lineHeight:"1.65", fontSize:13, color:"var(--fc-text-primary)", flex:1 }}>
            {parseInline(trimmed.replace(/^\d+\.\s/, ""))}
          </span>
        </div>
      );
      i++; continue;
    }
    if (/^[-*]\s/.test(trimmed)) {
      elements.push(
        <div key={i} style={{ display:"flex", gap:9, marginBottom:4, alignItems:"flex-start" }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background:"#2563EB", flexShrink:0, marginTop:7 }} />
          <span style={{ lineHeight:"1.65", fontSize:13, color:"var(--fc-text-primary)" }}>
            {parseInline(trimmed.slice(2))}
          </span>
        </div>
      );
      i++; continue;
    }
    if (trimmed.startsWith("> ")) {
      elements.push(
        <div key={i} style={{ borderLeft:"3px solid #2563EB", paddingLeft:10, margin:"8px 0", color:"var(--fc-text-secondary)", fontSize:"12.5px", lineHeight:"1.6", background:"var(--fc-bg-light)", borderRadius:"0 6px 6px 0", padding:"6px 10px" }}>
          {parseInline(trimmed.slice(2))}
        </div>
      );
      i++; continue;
    }
    if (trimmed === "") { elements.push(<div key={i} style={{ height:5 }} />); i++; continue; }
    elements.push(
      <p key={i} style={{ margin:"2px 0", lineHeight:"1.65", fontSize:13, color:"var(--fc-text-primary)" }}>
        {parseInline(trimmed)}
      </p>
    );
    i++;
  }
  return (
    <div style={{ padding:"14px 16px", borderRadius:"4px 14px 14px 14px", background:"var(--fc-bg-light)", border:"0.5px solid var(--fc-border-light)" }}>
      {elements}
    </div>
  );
}

// ── Job Fit Result ────────────────────────────────────────────────────────────
const BADGE_STYLES = {
  "Strong fit": { bg:"var(--fc-green-light)", color:"var(--fc-green-text)" },
  "Good fit":   { bg:"var(--fc-amber-light)", color:"var(--fc-amber-text)" },
  "Partial fit":{ bg:"var(--fc-amber-light)", color:"var(--fc-amber-text)" },
  "Poor fit":   { bg:"var(--fc-red-light)", color:"var(--fc-red-text)" },
};
const STATUS_STYLES = {
  match:   { dot:"#2f7a3c", pill:{ bg:"var(--fc-green-light)", color:"var(--fc-green-text)" }, label:"Match" },
  gap:     { dot:"#b3402e", pill:{ bg:"var(--fc-red-light)", color:"var(--fc-red-text)" }, label:"Gap" },
  partial: { dot:"#b5750f", pill:{ bg:"var(--fc-amber-light)", color:"var(--fc-amber-text)" }, label:"Partial" },
};

function JobFitResult({ data }) {
  const fitStyle = BADGE_STYLES[data.fit_label] || BADGE_STYLES["Partial fit"];
  const barColor = data.fit_score >= 70 ? "#2f7a3c" : data.fit_score >= 45 ? "#b5750f" : "#b3402e";
  return (
    <div style={{ fontSize:13 }}>
      <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border-strong)", borderRadius:12, padding:"14px 18px", marginBottom:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:data.summary?10:0 }}>
          <div>
            <div style={{ fontSize:22, fontWeight:"500", color:"var(--fc-text-primary)", lineHeight:1 }}>{data.fit_score}%</div>
            <div style={{ fontSize:10, color:"var(--fc-text-subtle)", marginTop:2 }}>match</div>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ height:6, background:"var(--fc-border-light)", borderRadius:3, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${data.fit_score}%`, background:barColor, borderRadius:3 }} />
            </div>
          </div>
          <span style={{ fontSize:10, padding:"3px 9px", borderRadius:100, fontWeight:"500", background:fitStyle.bg, color:fitStyle.color, flexShrink:0 }}>
            {data.fit_label}
          </span>
        </div>
        {data.summary && <p style={{ color:"var(--fc-text-secondary)", fontSize:"12.5px", lineHeight:"1.6", margin:0 }}>{data.summary}</p>}
      </div>

      {data.items?.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:8 }}>
          {data.items.map((item, i) => {
            const s = STATUS_STYLES[item.status] || STATUS_STYLES.partial;
            return (
              <div key={i} style={{ background:"var(--fc-card-bg)", border:"0.5px solid var(--fc-border-light)", borderRadius:12, padding:"10px 14px", display:"flex", alignItems:"flex-start", gap:10 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:s.dot, flexShrink:0, marginTop:5 }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:"500", fontSize:13, color:"var(--fc-text-primary)", marginBottom:2 }}>{item.label}</div>
                  <div style={{ fontSize:"11.5px", color:"var(--fc-text-subtle)", borderLeft:"2px solid var(--fc-border-light)", paddingLeft:9, lineHeight:"1.5" }}>{item.detail}</div>
                </div>
                <span style={{ fontSize:10, padding:"3px 9px", borderRadius:100, fontWeight:"500", flexShrink:0, background:s.pill.bg, color:s.pill.color }}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {data.has_gaps && data.training_plan?.length > 0 && (
        <>
          <div style={{ fontSize:"11.5px", fontWeight:"500", color:"var(--fc-text-secondary)", margin:"10px 0 6px", paddingLeft:2 }}>Training plan to close the gaps</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {data.training_plan.map((t, i) => (
              <div key={i} style={{ background:"var(--fc-card-bg)", border:"0.5px solid var(--fc-border-light)", borderRadius:12, padding:"10px 14px" }}>
                <div style={{ fontWeight:"500", fontSize:13, color:"var(--fc-text-primary)", marginBottom:3, display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ color:"#2563EB" }}>📚</span>{t.title}
                </div>
                <div style={{ fontSize:"11.5px", color:"var(--fc-text-subtle)", borderLeft:"2px solid var(--fc-border-light)", paddingLeft:9, lineHeight:"1.5" }}>{t.detail}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main ConnectIQ Tab ────────────────────────────────────────────────────────
export default function ConnectIQTab({ resourceSlug, candidateName, rd }) {
  const { user, userSlug } = useAuth();
  const userInitials = user?.first_name
    ? `${(user.first_name[0]||"")}${(user.last_name?.[0]||"")}`.toUpperCase()
    : "ME";

  const [sessions,    setSessions]    = useState([]);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sessionId,   setSessionId]   = useState(null);
  const [messages,    setMessages]    = useState([]);
  const [input,       setInput]       = useState("");
  const [sending,     setSending]     = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const endRef    = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    if (!userSlug || !resourceSlug) { setInitLoading(false); return; }
    connectiqGetProfileSessions(userSlug, resourceSlug)
      .then(res => {
        const list = res?.data?.sessions || [];
        setSessions(list);
        if (list.length > 0) loadSession(list[0].session_id);
        setInitLoading(false);
      })
      .catch(() => setInitLoading(false));
  }, [userSlug, resourceSlug]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages, sending]);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + "px";
    }
  }, [input]);

  const loadSession = (sid) => {
    setSessionId(sid); setMessages([]); setLoadingMsgs(true);
    setMobileSidebarOpen(false);
    connectiqGetProfileHistory(userSlug, sid)
      .then(res => { setMessages(res?.data?.messages || []); setLoadingMsgs(false); })
      .catch(() => setLoadingMsgs(false));
  };

  const newChat = async () => {
    setMobileSidebarOpen(false);
    const res = await connectiqCreateProfileSession(userSlug, resourceSlug, candidateName);
    if (res?.data?.status === 1) {
      const s = { session_id:res.data.session_id, session_title:"New chat", updated_at:new Date().toISOString() };
      setSessions(p => [s, ...p]);
      setSessionId(res.data.session_id);
      setMessages([]);
    }
  };

  const handleDeleteSession = async (e, sid) => {
    e.stopPropagation();
    await connectiqDeleteSession(userSlug, sid);
    setSessions(prev => prev.filter(s => s.session_id !== sid));
    if (sessionId === sid) { setSessionId(null); setMessages([]); }
  };

  const buildProfile = () => {
    if (!rd) return `Candidate: ${candidateName}`;
    const pd = rd.profile_details || {};
    let s = `Name: ${pd.first_name||""} ${pd.last_name||""}\nRole: ${pd.user_profile_role||""}\nBio: ${pd.profile_bio||""}\nAvailability: ${rd.availability_details?.availability||0} hrs/wk\n`;
    if (rd.skill_details?.length) s += `Skills: ${rd.skill_details.map(x=>x.skill_name).join(", ")}\n`;
    if (rd.education_details?.length) s += `Education: ${rd.education_details.map(e=>`${e.degree_name} in ${e.subject} from ${e.school_name}`).join("; ")}\n`;
    if (rd.employment_details?.length) s += `Employment: ${rd.employment_details.map(e=>`${e.job_title} at ${e.employer_name}`).join("; ")}\n`;
    return s;
  };

  const doSend = useCallback(async (text) => {
    if (!text || sending) return;
    let sid = sessionId;
    if (!sid) {
      const res = await connectiqCreateProfileSession(userSlug, resourceSlug, candidateName);
      if (res?.data?.status === 1) {
        sid = res.data.session_id;
        setSessions(p => [{ session_id:sid, session_title:text.slice(0,50), updated_at:new Date().toISOString() }, ...p]);
        setSessionId(sid);
      }
    }
    setMessages(p => [...p, { role:"user", type:"text", content:text }]);
    setSending(true);
    const res = await connectiqProfileChat(userSlug, text, buildProfile(), resourceSlug, candidateName, sid);
    if (res?.data?.status === 1) {
      setSessions(p => p.map(s => s.session_id===sid ? { ...s, session_title:s.session_title==="New chat"?text.slice(0,50):s.session_title, updated_at:new Date().toISOString() } : s));
      if (res.data.type === "job_fit") {
        setMessages(p => [...p, { role:"assistant", type:"job_fit", data:res.data.data }]);
      } else {
        setMessages(p => [...p, { role:"assistant", type:"text", content:res.data.message }]);
      }
    } else {
      setMessages(p => [...p, { role:"assistant", type:"text", content:"Something went wrong. Please try again." }]);
    }
    setSending(false);
  }, [sending, sessionId, userSlug, resourceSlug, candidateName]);

  const handleSend = () => { const t = input.trim(); if (!t) return; setInput(""); doSend(t); };
  const handleKey  = (e) => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  if (initLoading) return (
    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"20px 0", color:"var(--fc-text-subtle)", fontSize:13 }}>
      <div style={{ width:28, height:28, borderRadius:7, background:"#171717", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:10, fontWeight:"500" }}><i className="fa-solid fa-robot" style={{fontSize:11}} /></div>
      Loading ConnectIQ...
    </div>
  );

  return (
    <div style={{ display:"flex", minHeight:500, border:"0.5px solid var(--fc-border-light)", borderRadius:12, overflow:"hidden", fontFamily:"inherit", position:"relative" }}>
      {/* Mobile backdrop — tap to dismiss the chat history drawer */}
      {mobileSidebarOpen && (
        <div className="d-md-none" onClick={() => setMobileSidebarOpen(false)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:499, backdropFilter:"blur(2px)" }} />
      )}

      {/* Sidebar */}
      <div className={`fc-connectiqtab-sidebar${mobileSidebarOpen ? " mobile-open" : ""}`}
        style={{ width:220, flexShrink:0, background:"var(--fc-card-bg)", borderRight:"0.5px solid var(--fc-border-light)", display:"flex", flexDirection:"column" }}>
        <div style={{ padding:"14px 12px 10px", borderBottom:"0.5px solid var(--fc-border-light)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:10 }}>
            <div style={{ width:22, height:22, borderRadius:5, background:"#171717", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:9, fontWeight:"600" }}><i className="fa-solid fa-robot" style={{fontSize:9}} /></div>
            <span style={{ fontSize:"12.5px", fontWeight:"500", color:"var(--fc-text-primary)", flex:1 }}>Connect IQ</span>
            <button onClick={() => setMobileSidebarOpen(false)} className="d-md-none"
              style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-text-subtle)", fontSize:14, padding:2, lineHeight:1 }}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <button onClick={newChat} style={{ width:"100%", padding:"7px 10px", background:"#171717", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:"500", cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
            + New chat
          </button>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"8px 0" }}>
          {sessions.length === 0
            ? <div style={{ padding:12, fontSize:"11.5px", color:"var(--fc-text-subtle)", textAlign:"center" }}>No chats yet</div>
            : sessions.map(s => (
              <div key={s.session_id} onClick={() => loadSession(s.session_id)}
                style={{ padding:"8px 12px", cursor:"pointer", background:sessionId===s.session_id?"var(--fc-bg-light)":"transparent", borderLeft:`2px solid ${sessionId===s.session_id?"#171717":"transparent"}`, transition:"all 0.1s" }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                  <div style={{ width:5, height:5, borderRadius:"50%", background:"var(--fc-text-hint)", flexShrink:0 }} />
                  <div style={{ fontSize:12, color:"var(--fc-text-primary)", fontWeight:sessionId===s.session_id?"500":"400", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>
                    {s.session_title||"New chat"}
                  </div>
                  <button onClick={(e) => handleDeleteSession(e, s.session_id)}
                    title="Delete chat"
                    style={{ background:"none", border:"none", color:"var(--fc-text-hint)", cursor:"pointer", fontSize:12, lineHeight:1, flexShrink:0, padding:2 }}
                    onMouseEnter={e=>e.currentTarget.style.color="var(--fc-red)"}
                    onMouseLeave={e=>e.currentTarget.style.color="var(--fc-text-hint)"}>
                    <i className="fa-solid fa-trash-can" />
                  </button>
                </div>
                <div style={{ fontSize:"10.5px", color:"var(--fc-text-subtle)", paddingLeft:11 }}>{timeAgo(s.updated_at)}</div>
              </div>
            ))
          }
        </div>
      </div>

      {/* Chat area */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", background:"var(--fc-card-bg)", minWidth:0 }}>
        <div style={{ padding:"12px 16px", borderBottom:"0.5px solid var(--fc-border-light)", display:"flex", alignItems:"center", gap:8 }}>
          <button onClick={() => setMobileSidebarOpen(true)} className="d-md-none"
            style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-text-primary)", fontSize:14, padding:2, lineHeight:1, flexShrink:0 }}>
            <i className="fa-solid fa-bars" />
          </button>
          <div style={{ fontSize:13, fontWeight:"500", color:"var(--fc-text-primary)" }}>
            {sessionId ? (sessions.find(s=>s.session_id===sessionId)?.session_title||"New chat") : "Start a new chat"}
          </div>
          <div style={{ fontSize:"11.5px", color:"var(--fc-text-subtle)", marginLeft:"auto" }}>{candidateName}</div>
        </div>

        <div style={{ flex:1, overflowY:"auto", padding:16, display:"flex", flexDirection:"column", gap:14 }}>
          {!sessionId && messages.length===0 && (
            <div style={{ textAlign:"center", color:"var(--fc-text-subtle)", fontSize:13, marginTop:40 }}>Ask anything about {candidateName}</div>
          )}
          {loadingMsgs && <div style={{ textAlign:"center", color:"var(--fc-text-subtle)", fontSize:12 }}>Loading messages...</div>}

          {messages.map((msg, i) => msg.role==="user" ? (
            <div key={i} style={{ display:"flex", justifyContent:"flex-end", gap:10, alignItems:"flex-start" }}>
              <div style={{ maxWidth:"78%", padding:"10px 14px", borderRadius:"14px 4px 14px 14px", background:"#171717", color:"#fff", fontSize:13, lineHeight:"1.6" }}>
                {msg.content}
              </div>
              <div style={{ width:28, height:28, borderRadius:"50%", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:"500", background:"var(--fc-bg-light)", color:"var(--fc-text-muted)" }}>
                {userInitials}
              </div>
            </div>
          ) : (
            <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
              <div style={{ width:28, height:28, borderRadius:"50%", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:"500", background:"#171717", color:"#fff" }}><i className="fa-solid fa-robot" style={{fontSize:11}} /></div>
              <div style={{ flex:1 }}>
                {msg.type==="job_fit" ? <JobFitResult data={msg.data} /> : renderTextContent(msg.content)}
              </div>
            </div>
          ))}

          {sending && (
            <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
              <div style={{ width:28, height:28, borderRadius:"50%", background:"#171717", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:"500", color:"#fff", flexShrink:0 }}><i className="fa-solid fa-robot" style={{fontSize:11}} /></div>
              <div style={{ padding:"12px 16px", background:"var(--fc-bg-light)", borderRadius:"4px 14px 14px 14px", display:"flex", gap:4, alignItems:"center" }}>
                {[0,1,2].map(j => <div key={j} style={{ width:6, height:6, borderRadius:"50%", background:"var(--fc-text-hint)", animation:`ciq-bounce 1.2s ease-in-out ${j*0.2}s infinite` }} />)}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div style={{ padding:"12px 16px", borderTop:"0.5px solid var(--fc-border-light)" }}>
          <div style={{ background:"var(--fc-card-bg)", borderRadius:12, display:"flex", alignItems:"flex-end", gap:8, padding:"10px 10px 10px 14px", border:"1px solid var(--fc-border)" }}>
            <textarea ref={inputRef} rows={1} value={input}
              onChange={e=>setInput(e.target.value)} onKeyDown={handleKey}
              placeholder={`Ask anything about ${candidateName}...`}
              disabled={sending}
              style={{ flex:1, background:"transparent", border:"none", outline:"none", color:"var(--fc-text-primary)", fontSize:13, resize:"none", lineHeight:"1.5", maxHeight:120, fontFamily:"inherit" }} />
            <button onClick={handleSend} disabled={!input.trim()||sending}
              style={{ width:30, height:30, borderRadius:8, background:!input.trim()||sending?"var(--fc-border-light)":"#171717", border:"none", color:!input.trim()||sending?"var(--fc-text-hint)":"#fff", cursor:!input.trim()||sending?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:14 }}>
              ↑
            </button>
          </div>
        </div>
      </div>

      <style>{`@keyframes ciq-bounce{0%,60%,100%{transform:translateY(0);opacity:0.4}30%{transform:translateY(-5px);opacity:1}}`}</style>
    </div>
  );
}